"use client";

import { useContactPageRouterContext } from "../../../helpers/ContactPageRouterContext";
import { analyticsClick } from "../../../helpers/analytics";
import type { FurtherResourceItem } from "../../../helpers/data/faqs/getFurtherResources";
import { CategoryHeader } from "../CategoryHeader/CategoryHeader";
import { FurtherResourcesSection } from "../FurtherResourcesSection/FurtherResourcesSection";
import { Question } from "../Question/Question";
import { SearchResultsSection } from "../SearchResultsSection/SearchResultsSection";
import type { TypesenseSearchFieldProps } from "../TypesenseSearchField/TypesenseSearchField";
import classNames from "classnames/bind";
import styles from "./SupportHubHomeFeatured.module.css";

const cx = classNames.bind(styles);

type FeaturedQuestion = {
  uuid: string;
  name: string;
  question: string;
  tags: string[];
};

export function SupportHubHomeFeatured({
  localeString,
  featuredQuestionsTitle,
  featuredQuestions,
  featuredFurtherResources,
  featuredFurtherResourcesTitle,
  featuredFurtherResourcesDescription,
  typesenseSearchEnabled = false,
  searchQuery = null,
  searchInputLabel,
  searchInputPlaceholderLabel,
  searchSeeAllResultsButtonLabel,
  searchNoResultsLabel,
  searchModalCloseLabel,
}: {
  localeString: string;
  featuredQuestionsTitle: string;
  featuredQuestions: FeaturedQuestion[];
  featuredFurtherResources: FurtherResourceItem[];
  featuredFurtherResourcesTitle?: string;
  featuredFurtherResourcesDescription?: string;
  typesenseSearchEnabled?: boolean;
  searchQuery?: string | null;
} & Pick<
  TypesenseSearchFieldProps,
  | "searchInputLabel"
  | "searchInputPlaceholderLabel"
  | "searchSeeAllResultsButtonLabel"
  | "searchNoResultsLabel"
  | "searchModalCloseLabel"
>) {
  const contactPageRouter = useContactPageRouterContext();

  if (typesenseSearchEnabled && searchQuery) {
    return (
      <div className={cx("container")} data-support-hub-page="search">
        <SearchResultsSection
          localeString={localeString}
          searchQuery={searchQuery}
          searchInputLabel={searchInputLabel}
          searchInputPlaceholderLabel={searchInputPlaceholderLabel}
          searchSeeAllResultsButtonLabel={searchSeeAllResultsButtonLabel}
          searchNoResultsLabel={searchNoResultsLabel}
          searchModalCloseLabel={searchModalCloseLabel}
        />
      </div>
    );
  }

  const hasQuestions = featuredQuestions.length > 0;
  const hasFurtherResources = featuredFurtherResources.length > 0;

  if (!hasQuestions && !hasFurtherResources) {
    return null;
  }

  return (
    <div className={cx("container")}>
      {hasQuestions && (
        <section className={cx("section")}>
          <CategoryHeader
            verticalAlign="center"
            title={featuredQuestionsTitle}
            height="short"
          />

          <ol className={cx("questions")} role="list">
            {featuredQuestions.map((question) => {
              const tags = question.tags;

              return (
                <li key={question.uuid} className={cx("question-item")}>
                  <div className={cx("question-cell")}>
                    <Question
                      question={question.question}
                      tags={tags}
                      href={contactPageRouter.selectQuestionUrl({
                        uuid: question.uuid,
                      })}
                      onClick={() => {
                        analyticsClick({
                          value: `Support hub home | ${question.name} | ${tags.length ? tags.join(", ") : ""}`,
                          clickUrl: null,
                          elementPosition: "page body",
                          method: "Outbound: FALSE",
                          contentType: "question",
                        });
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      )}

      {hasFurtherResources && (
        <FurtherResourcesSection
          furtherResourcesTitle={featuredFurtherResourcesTitle}
          furtherResourcesDescription={featuredFurtherResourcesDescription}
          furtherResources={featuredFurtherResources}
        />
      )}
    </div>
  );
}
